import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { useAuth } from '../../App';
import { Seo } from '../../components/seo/Seo';
import { PublicLayout } from '../../components/public/PublicLayout';
import { Container, CtaBand } from '../../components/public/PublicSections';
import { ArticleCard } from '../../components/public/ArticleCard';
import { LAYOUT_COPY } from '../../seo/publicContent';
import { listArticles, tipsSubtitle, TIPS_COPY, TIPS_HERO_IMAGE, AXIS_NAMES } from '../../seo/articles';
import { pageMeta, pagePath, alternatesFor, absoluteUrl } from '../../seo/seoConfig.mjs';
import { breadcrumbSchema } from '../../seo/structuredData';

const collectionSchema = (lang, articles, meta) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: meta.title,
  description: meta.description,
  url: absoluteUrl(pagePath('tips', lang)),
  inLanguage: lang,
  hasPart: articles.map(a => ({ '@type': 'Article', headline: a.title, url: absoluteUrl(a.path), image: absoluteUrl(a.cover) })),
});

export default function PublicTipsPage({ lang }) {
  const { user } = useAuth();
  const copy = TIPS_COPY[lang];
  const meta = pageMeta('tips', lang);
  const nav = LAYOUT_COPY[lang].nav;
  const articles = listArticles(lang);
  const appHref = user ? (user.onboarding_complete ? '/dashboard' : '/onboarding') : '/auth';
  const axes = [...new Set(articles.map(a => a.axis))];

  return (
    <PublicLayout lang={lang} pageKey="tips">
      <Seo
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        path={pagePath('tips', lang)}
        lang={lang}
        alternates={alternatesFor('tips')}
        jsonLd={[
          breadcrumbSchema([{ name: nav.home, path: pagePath('home', lang) }, { name: nav.tips, path: pagePath('tips', lang) }]),
          collectionSchema(lang, articles, meta),
        ]}
      />

      <section className="relative overflow-hidden" aria-labelledby="tips-title">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(600px circle at 15% 0%, rgba(243,179,61,0.12), transparent 55%)' }} />
        <Container className="relative pt-14 pb-12 sm:pt-20 sm:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--lux-gold)] mb-3">{copy.eyebrow}</p>
            <h1 id="tips-title" className="font-display text-4xl sm:text-5xl leading-[1.08] text-white" data-testid="tips-title">{copy.h1}</h1>
            <p className="mt-5 text-base sm:text-lg text-white/60 leading-relaxed max-w-2xl" data-testid="tips-subtitle">{tipsSubtitle(lang)}</p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="tips-axis-chips">
              {axes.map(axis => (
                <a key={axis} href={`#axis-${axis}`} className="px-3 py-1.5 rounded-full border border-white/10 bg-[var(--lux-panel)] text-xs text-white/70 hover:text-[var(--lux-gold)] hover:border-lux-gold/40 transition-colors duration-200" data-testid={`tips-axis-chip-${axis}`}>
                  {copy.byAxis} {axis} {'\u00b7'} {AXIS_NAMES[lang][axis]}
                </a>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_12px_32px_rgba(0,0,0,0.55)] aspect-[4/3] bg-[var(--lux-panel)]">
              <img src={TIPS_HERO_IMAGE} alt={copy.heroAlt} width="1400" height="1050" className="w-full h-full object-cover opacity-85" loading="eager" />
            </div>
          </div>
        </Container>
      </section>

      <section className="pb-8" aria-label={copy.allTips}>
        <Container className="space-y-14">
          {axes.map(axis => (
            <div key={axis} id={`axis-${axis}`} className="scroll-mt-24" data-testid={`tips-axis-group-${axis}`}>
              <h2 className="font-display text-2xl sm:text-3xl text-white">{copy.byAxis} {axis} {'\u00b7'} {AXIS_NAMES[lang][axis]}</h2>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.filter(a => a.axis === axis).map(a => <ArticleCard key={a.key} article={a} lang={lang} />)}
              </div>
            </div>
          ))}
        </Container>
      </section>

      <section className="py-12">
        <Container>
          <div className="rounded-2xl border border-lux-gold/25 bg-lux-gold/5 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="tips-guide-link">
            <span className="w-12 h-12 rounded-xl bg-lux-gold/10 text-[var(--lux-gold)] flex items-center justify-center shrink-0"><BookOpen className="w-6 h-6" /></span>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white">{copy.guideLinkTitle}</h2>
              <p className="mt-1 text-sm text-white/55">{copy.guideLinkText}</p>
            </div>
            <Link to={pagePath('guide', lang)} className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--lux-gold)] hover:text-[var(--lux-amber)] transition-colors duration-200" data-testid="tips-guide-cta">
              {copy.guideLinkCta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Container>
      </section>

      <CtaBand title={copy.ctaTitle} sub={copy.ctaText} ctaLabel={copy.ctaLabel} to={appHref} testId="tips-final-cta" />
    </PublicLayout>
  );
}
